import { useContentfulLiveUpdates } from '@contentful/live-preview/react';
import type { ReactElement } from 'react';
import { Fragment, useContext, useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import useMemoizedFn from '@/utils/useMemoizedFn';

import { Container } from '@/components/Container';
import { Flex } from '@/components/Flex';
import { Loader } from '@/components/Loader';
import type { ProductFavoriteProps } from '@/components/ProductCard';
import { FixedPageDocument } from '@/graphql/contentful/api';
import type { LanguageCodeEnum } from '@/graphql/saleor/api';
import { useGoogleAnalytics } from '@/hooks/useGoogleAnalytics';
import { useIsUnderLargeBreakpoint } from '@/hooks/useMedia';
import { useRouter } from '@/hooks/useRouter';
import { useUser } from '@/hooks/useUser';
import { useWishList } from '@/hooks/useWishlist';
import { getFilterReleasedProducts } from '@/integrations/algolia/helpers';
import type { EyewearHit, WatchHit } from '@/integrations/algolia/types';
import type { ContentfulData } from '@/integrations/contentful';
import {
  type ContentfulFixedPageCMSData,
  generateSectionKey,
  renderContentfulFixedPage,
} from '@/integrations/contentful';
import { AccountLayout } from '@/layouts/AccountLayout';
import { fetchProductsBySkus } from '@/lib/product/api';
import { generateDataTestId } from '@/lib/tests';
import { formatProductSlug } from '@/lib/util';
import { commonMessages } from '@/messages/common';
import { myAccountMessages } from '@/messages/myAccount';
import * as M from '@/messages/wishlist';
import { DraftModeContext } from '@/providers/DraftModeProvider/DraftModeContext';
import { LocaleContext } from '@/providers/LocaleProvider';
import { useRegionProvider } from '@/providers/RegionProvider';
import { useSearchState } from '@/states/searchState';
import {
  getLocalWishlistByCountry,
  reorderProductsBySkuList,
  sortLocalWishlist,
} from '@/views/WishListView/utils';

import type { ProductWithcompare } from './components/WishListWrapper/WishListWrapper';
import { WishlistWrapper } from './components/WishListWrapper/WishListWrapper';
import type { WishlistItem } from './types';

import * as S from './WishListView.styles';

export interface WishListViewProps {
  cms?: ContentfulFixedPageCMSData;
  cmsDefaultLayoutData?: ContentfulData[];
  skus?: string[];
  straps?: string[];
}

export const fetchWishlistData = async (
  watchSkuList: string[],
  strapList: string[],
  eyewearOriginSkuList: string[],
  languageCode: LanguageCodeEnum,
) => {
  try {
    const eyewearSkuList = eyewearOriginSkuList.map((item) =>
      item.replace('.', '_'),
    );
    // Only fetch data for non-empty lists to avoid undefined results
    const strapsPromise =
      strapList.length > 0
        ? fetchProductsBySkus({ skus: strapList, languageCode })
        : Promise.resolve({ products: { edges: [] } });

    const watchesPromise =
      watchSkuList.length > 0
        ? fetchProductsBySkus({ skus: watchSkuList, languageCode })
        : Promise.resolve({ products: { edges: [] } });

    const eyewearPromise =
      eyewearSkuList.length > 0
        ? fetchProductsBySkus({ skus: eyewearSkuList, languageCode })
        : Promise.resolve({ products: { edges: [] } });

    const [strapsData, watchesData, eyewearData] = await Promise.all([
      strapsPromise,
      watchesPromise,
      eyewearPromise,
    ]);

    return {
      straps: reorderProductsBySkuList(strapsData?.products, strapList) || {
        edges: [],
      },
      watches: reorderProductsBySkuList(
        watchesData?.products,
        watchSkuList,
      ) || { edges: [] },
      eyewear: reorderProductsBySkuList(
        eyewearData?.products,
        eyewearOriginSkuList,
      ) || { edges: [] },
    };
  } catch (error) {
    console.error('Error fetching wishlist data:', error);
    return {
      straps: { edges: [] },
      watches: { edges: [] },
      eyewear: { edges: [] },
    };
  }
};

export const WishListView = ({ cms, skus, straps }: WishListViewProps) => {
  const liveCms = useContentfulLiveUpdates(cms, {
    query: FixedPageDocument,
  });
  const locale = useContext(LocaleContext);
  const { country, query } = useRegionProvider();
  const isUnderLargeBreakpoint = useIsUnderLargeBreakpoint();
  const { formatMessage } = useIntl();
  const {
    fetchUser,
    snapshot: {
      isAuthenticated,
      isAuthenticating,
      isInitializing,
      isProcessing,
    },
  } = useUser();
  const isAuthLoading = isAuthenticating || isInitializing || isProcessing;
  const {
    getWishListForAll,
    removeItemFromLocalWishlist,
    removeItemFromServerWishlist,
  } = useWishList({ countryCode: country.code });
  const { sendWishlistComparisonStartEvent } = useGoogleAnalytics();
  const [compareList, setCompareList] = useState<string[]>([]);
  const { paths, push } = useRouter();
  const [productList, setProductList] = useState<ProductWithcompare[]>([]);
  const [isEmpty, setIsEmpty] = useState<boolean | undefined>(undefined);
  const [isInited, setIsInited] = useState<boolean>(false);
  const [algoliaLoading, setAlgoliaLoading] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const { overlayStatus } = useSearchState();
  let wishlist: WishlistItem[] = [];
  wishlist = sortLocalWishlist(getLocalWishlistByCountry(country.code));

  let watchSkuList: string[] = []; // Watches stored in localStorage
  let strapList: string[] = [];
  let eyewearSkuList: string[] = []; // Eyewear stored in localStorage

  const isPublicWishlist = !!(skus || straps);
  if (isPublicWishlist) {
    watchSkuList = skus ?? [];
    strapList = straps ?? [];
    // For public wishlist URLs, we don't know the product type yet,
    // so we'll handle separating them during filtering
  }

  const didInitRef = useRef(false);

  useEffect(() => {
    if (isAuthenticated === null) {
      fetchUser();
      return;
    }

    if (overlayStatus === 'CLOSE' && !didInitRef.current) {
      initWishList();
      didInitRef.current = true;
    }

    if (overlayStatus === 'OPEN') {
      didInitRef.current = false;
    }
  }, [isAuthenticated, overlayStatus]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        setIsInited(false);

        initWishList();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const initWishList = useMemoizedFn(async () => {
    setLoading(true);
    if (isAuthenticated) {
      wishlist = await getWishListForAll();
    }

    // Clear previous lists
    watchSkuList = [];
    strapList = [];
    eyewearSkuList = [];

    // Handle public wishlist URLs
    const isPublicWishlist = !!(skus || straps);
    if (isPublicWishlist) {
      // For public wishlist URLs, we'll make some assumptions about the product types
      if (straps) {
        strapList = straps;
      }
      if (skus) {
        // We don't know if these are watches or eyewear yet
        // We'll pass them to both filters and let Algolia sort them out
        watchSkuList = [...skus];
        eyewearSkuList = [...skus];
      }
      console.log('Public wishlist:', {
        strapList,
        watchSkuList,
        eyewearSkuList,
      });
    } else {
      // Process wishlist items by type
      for (const item of wishlist) {
        const itemType = item.type?.toLowerCase();
        if (itemType === 'strap') {
          strapList.push(item.sku);
        } else if (itemType === 'watch') {
          watchSkuList.push(item.sku);
        } else if (itemType === 'eyewear') {
          eyewearSkuList.push(item.sku);
        }
      }
    }

    try {
      const { eyewear, straps, watches } = await fetchWishlistData(
        watchSkuList,
        strapList,
        eyewearSkuList,
        query.languageCode,
      );

      let products: ProductWithcompare[] = [];

      // Process watch products
      if (watches && watchSkuList.length > 0) {
        const watchesList =
          watches?.edges?.map((item) => {
            return {
              ...item.node,
              compareType: false,
            };
          }) ?? [];

        // Get all SKUs from the fetched watches
        const watchProductSkus: string[] =
          watchesList
            ?.filter((item) => !!item.defaultVariant?.sku)
            ?.map((item) => item.defaultVariant?.sku as string) ?? [];

        if (watchProductSkus.length > 0) {
          setAlgoliaLoading(true);
          try {
            // Filter released watches through Algolia
            const watchFilterResult = await getFilterReleasedProducts<WatchHit>(
              watchSkuList,
              locale,
              'Watch',
            );

            const releasedWatchSkus = watchFilterResult.map(
              (hit) => hit.objectID,
            );

            if (releasedWatchSkus && releasedWatchSkus.length > 0) {
              products = products.concat(
                watchesList?.filter(
                  (item) =>
                    item.defaultVariant?.sku &&
                    releasedWatchSkus?.includes(item.defaultVariant?.sku),
                ),
              );
            }
          } catch (error) {
            console.error('Error filtering watches:', error);
          } finally {
            setAlgoliaLoading(false);
          }
        }
      }

      // Process eyewear products
      if (eyewear && eyewearSkuList.length > 0) {
        const eyewearList =
          eyewear?.edges?.map((item) => {
            return {
              ...item.node,
              compareType: false,
            };
          }) ?? [];

        // Get all SKUs from the fetched eyewear
        const eyewearProductSkus: string[] =
          eyewearList
            ?.filter((item) => !!item.defaultVariant?.sku)
            ?.map((item) => item.defaultVariant?.sku as string) ?? [];

        if (eyewearProductSkus.length > 0) {
          setAlgoliaLoading(true);
          try {
            // Filter released eyewear through Algolia
            const eyewearFilterResult =
              await getFilterReleasedProducts<EyewearHit>(
                eyewearSkuList,
                locale,
                'Eyewear',
              );

            const releasedEyewearSkus = eyewearFilterResult.map(
              (hit) => hit.objectID,
            );

            if (releasedEyewearSkus && releasedEyewearSkus.length > 0) {
              products = products.concat(
                eyewearList?.filter(
                  (item) =>
                    item.defaultVariant?.sku &&
                    releasedEyewearSkus?.includes(item.defaultVariant?.sku),
                ),
              );
            }
          } catch (error) {
            console.error('Error filtering eyewear:', error);
          } finally {
            setAlgoliaLoading(false);
          }
        }
      }

      // Process strap products
      if (straps && strapList.length > 0) {
        const strapsList =
          straps?.edges?.map((item) => {
            return {
              ...item.node,
            };
          }) ?? [];
        products = products.concat(strapsList);
      }

      setProductList(products);
    } finally {
      setLoading(false);
      setIsInited(true);
    }
  });

  useEffect(() => {
    setIsEmpty(
      watchSkuList.length === 0 &&
        strapList.length === 0 &&
        eyewearSkuList.length === 0,
    );
  }, [watchSkuList.length, strapList.length, eyewearSkuList.length]);

  const toCompare = () => {
    const queryParams: Record<string, string> = {};
    if (compareList.length > 0) {
      queryParams['skus'] = compareList.join();

      sendWishlistComparisonStartEvent({
        skus: compareList,
        wishlistNumberProductsCompared: compareList.length,
      });
    }
    push(paths.watchComparison.url({ query: queryParams }));
  };

  const handeChecked = (
    e: {
      preventDefault: () => void;
      target: any;
    },
    product: ProductWithcompare,
  ) => {
    if (e.target.checked == undefined) {
      handleChange(product);
    }
    e.preventDefault();
  };

  const handleChange = (product: ProductWithcompare) => {
    let list = productList;
    const arr: string[] = compareList || [];
    list = list.map((item) => {
      if (item.slug == product.slug) {
        if (item.compareType) {
          const index = arr.findIndex(
            (compareItem) => compareItem == product.slug,
          );
          arr.splice(index, 1);
        } else {
          arr.push(item.slug!);
        }
        item.compareType = !item.compareType;
      }
      return item;
    });

    setCompareList([...arr]);
    setProductList([...list]);
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  const removeFavoriteItem = (
    e: { preventDefault: () => void; stopPropagation: () => void },
    product: ProductFavoriteProps,
  ) => {
    e.stopPropagation();
    scrollToTop();
    if (isAuthenticated) {
      removeItemFromServerWishlist(product.sku as string);
    }
    removeItemFromLocalWishlist(product.sku);

    const productSlug = formatProductSlug(product.sku);
    const wishlistArr = productList.filter((item) => item.slug != productSlug);

    setProductList(wishlistArr);
    e.preventDefault();
  };

  const isLoading = !isInited || isAuthLoading || loading || algoliaLoading;
  if (isLoading) {
    return <Loader data-test-id='WishListViewLoader' height={150} />;
  }
  return (
    <S.Containers data-test-id='WishListViewBox'>
      {liveCms?.topContent?.map((data, i) => (
        <Fragment key={generateSectionKey(data, i)}>
          {isLoading
            ? null
            : renderContentfulFixedPage(
                data,
                undefined,
                liveCms.cmsExtraData,
                !isEmpty,
                isAuthenticated,
              )}
        </Fragment>
      ))}
      <Container disableGutters>
        {(isLoading || productList.length > 0) && (
          <S.Wishlist data-test-id='WishListViewContainer'>
            <WishlistWrapper
              handeChecked={handeChecked}
              handleChange={handleChange}
              isClosable={!isPublicWishlist}
              products={productList}
              removeFavoriteItem={removeFavoriteItem}
            />
          </S.Wishlist>
        )}
        {!isLoading && productList.length === 0 && (
          <S.Box>
            <S.Prompt>
              {formatMessage(myAccountMessages.accountWishlistEmptyTitle)}
            </S.Prompt>

            <>
              <S.Text>
                {formatMessage(myAccountMessages.wishlistEmptyText1)}
              </S.Text>
              {isAuthenticated ? (
                <S.Text>
                  {formatMessage(myAccountMessages.wishlistEmptyText2)}
                </S.Text>
              ) : (
                <S.Text>
                  <S.TextUnderLine
                    data-test-id='EmptyProductTextUnderLine-1'
                    onClick={() => push(paths.myAccount.login.url())}
                  >
                    <FormattedMessage {...commonMessages.logIn} />
                  </S.TextUnderLine>
                  <S.TextOr data-test-id='EmptyProductTextOr'>{M.Or}</S.TextOr>
                  <S.TextUnderLine
                    data-test-id='EmptyProductTextUnderLine-2'
                    onClick={() => push(paths.myAccount.signIn.url())}
                  >
                    {M.CreateAnAccount}
                  </S.TextUnderLine>

                  {formatMessage(
                    myAccountMessages.wishlistEmptyText2NotAuthenticated,
                  )}
                </S.Text>
              )}
            </>

            <S.ContinueShoppingButton
              data-test-id='EmptyShoppingBagContinueShoppingButton'
              href={paths.watches.asPath()}
              label={formatMessage(
                myAccountMessages.accountWishlistContinueShoppingCta,
              )}
              variant='filled'
            />
          </S.Box>
        )}
        {compareList.length > 1 ? (
          <S.SelectedWrapper data-test-id='WishListViewSelectedWrapper'>
            <S.InnerWrapperBox>
              {isUnderLargeBreakpoint ? (
                <S.ComparisonButton
                  data-test-id='WishListViewComparisonMobileButton'
                  isUnderLargeBreakpoint={isUnderLargeBreakpoint}
                  onClick={() => toCompare()}
                  {...generateDataTestId(
                    'productCard',
                    'ComparisonMobileButton',
                  )}
                >
                  {M.Comparison} ({compareList.length})
                </S.ComparisonButton>
              ) : (
                <>
                  <Flex alignItems='center' justifyContent='center'>
                    <S.WatchBox>{compareList.length}</S.WatchBox>
                    <S.WatchLabel>{M.WatchesSelected}</S.WatchLabel>
                  </Flex>
                  <S.ComparisonButton
                    data-test-id='WishListViewStartComparisonButton'
                    onClick={() => toCompare()}
                  >
                    {M.StartComparison}
                  </S.ComparisonButton>
                </>
              )}
            </S.InnerWrapperBox>
          </S.SelectedWrapper>
        ) : (
          <></>
        )}
      </Container>
      {liveCms?.bottomContent?.map((data, i) => (
        <Fragment key={generateSectionKey(data, i)}>
          {isLoading
            ? null
            : renderContentfulFixedPage(
                data,
                undefined,
                liveCms.cmsExtraData,
                !isEmpty,
                isAuthenticated,
              )}
        </Fragment>
      ))}
    </S.Containers>
  );
};

WishListView.getLayout = (page: ReactElement) => (
  <DraftModeContext.Provider value={page.props.draftMode}>
    <AccountLayout cmsData={page.props.cmsDefaultLayoutData}>
      {page}
    </AccountLayout>
  </DraftModeContext.Provider>
);
